import { SagaIterator } from 'redux-saga';
import { all, call, fork, ForkEffect, getContext, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import * as api from '../../api/api';
import { logout } from '../auth/actions';
import { getTags } from '../config/selectors';
import { getAuth, getUniId } from '../selectors';
import { openToast } from '../ui/actions';
import * as actions from './actions';
import { ApplyActionTypes } from './types';
import {
  ApplicantOfferRequirementTask,
  ApplicantOfferRequirementTaskStatus,
  IApplicantOffer,
  IApplicantOfferPayment,
  ErrorResponseBody,
  ResolveDuplicateHandlerResponse,
  RouteStatusResponse,
} from 'types';

// Utility function to flatten document URLs
const flattenUrls = (docsMap?: Record<string, { signedUrls?: unknown }>): string[] => {
  const docs = Object.values(docsMap ?? {});
  const urls: string[] = [];

  for (const d of docs) {
    const s = (d as any)?.signedUrls;
    if (Array.isArray(s)) {
      for (const entry of s as unknown[]) {
        if (Array.isArray(entry)) {
          urls.push(...((entry as unknown[]).filter((u: unknown) => typeof u === 'string') as string[]));
        } else if (typeof entry === 'string') {
          urls.push(entry as string);
        }
      }
    }
  }

  return urls;
};

function* handleGetApplicationRecord(action: ReturnType<typeof actions.getApplicantRecord>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const res: any = yield call(
      api.getApplicantById,
      idToken,
      action.payload.userId,
      action.payload.applicationId,
      action.payload.uni,
    );
    if (res) {
      yield put(
        actions.getApplicantRecordSuccess(
          res.applicant,
          res.application,
          res.offers,
          res.documentUrls,
          res.agentAccessStatus,
          action.payload.uni,
        ),
      );
    }
  } catch (err: any) {
    console.log('err', err);
    yield put(actions.getApplicantRecordFailure(err.message));
  }
}

function* handleGetAuditLog(action: ReturnType<typeof actions.getApplyAuditLog>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetAuditLog');
    }
    const res: Awaited<ReturnType<typeof api.getApplyAuditLog>> = yield call(
      api.getApplyAuditLog,
      idToken,
      action.payload.uni,
      action.payload.applicant,
      action.payload.application,
    );
    if ('applyAuditLog' in res) {
      yield put(actions.getApplyAuditLogSuccess(res.applyAuditLog));
    }
  } catch (err: any) {
    yield put(actions.getApplyAuditLogFailure(err.message));
  }
}

function* handleGetOfferTimeline(action: ReturnType<typeof actions.getOfferTimeline>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetOfferTimeline');
    }
    const res: Awaited<ReturnType<typeof api.getOfferTimeline>> = yield call(
      api.getOfferTimeline,
      idToken,
      action.payload.uni,
      action.payload.applicant,
      action.payload.application,
      action.payload.offer,
    );
    if ('offerTimeline' in res) {
      yield put(actions.getOfferTimelineSuccess(res.offerTimeline));
    }
  } catch (err: any) {
    yield put(actions.getOfferTimelineFailure(err.message));
  }
}

function* handleGetApplyEmails(action: ReturnType<typeof actions.getApplyEmails>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetApplyEmails');
    }
    const res: Awaited<ReturnType<typeof api.getApplyEmails>> = yield call(
      api.getApplyEmails,
      idToken,
      action.payload.uni,
      action.payload.applicant,
    );
    if ('applyEmails' in res) {
      yield put(actions.getApplyEmailsSuccess(res.applyEmails));
    }
  } catch (err: any) {
    yield put(actions.getApplyAuditLogFailure(err.message));
  }
}

function* handleGetApplicantOffer(action: ReturnType<typeof actions.getApplicantOffer>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const res = yield call(
      api.getApplicantOfferById,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
    );
    if (res && 'error' in res) {
      const errorMessage = typeof res.error === 'string' ? res.error : 'Failed to fetch offer';
      yield put(actions.getApplicantOfferFailure(errorMessage));
    } else if (res) {
      yield put(actions.getApplicantOfferSuccess(res));
    }
  } catch (err: any) {
    yield put(actions.getApplicantOfferFailure(err.message));
  }
}
function* handleGetApplicantIntakes(action: ReturnType<typeof actions.getApplicantIntakes>): any {
  try {
    if (!action.payload.uni) {
      yield put(actions.getApplicantIntakesFailure('No university specified'));
      return;
    }

    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetApplicantIntakes');
    }
    const res = yield call(api.getActiveIntakesWithCourses, idToken, action.payload.uni, action.payload.feeStatus);

    if (res && res.data && res.data.intakeIds) {
      // Backend filters to only active intakes with courses (and by fee status if provided)
      yield put(actions.getApplicantIntakesSuccess(res.data.intakeIds));
    } else {
      yield put(actions.getApplicantIntakesFailure('No active intakes found with courses'));
    }
  } catch (err: any) {
    yield put(actions.getApplicantIntakesFailure(err.message));
  }
}
function* handleGetApplyIntakes(action: ReturnType<typeof actions.getApplyIntakes>): any {
  try {
    console.log('Handling GET_APPLY_INTAKES for university:', action.payload.uni);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetApplicantIntakes');
    }
    const res = yield call(api.getConfig, idToken, action.payload.uni);

    if (res && res.intakeIds) {
      // Extract intakes from the university config
      const intakeOptions = res.intakeIds.map(intake => ({
        value: intake.value,
        label: intake.label,
      }));
      yield put(actions.getApplyIntakesSuccess(intakeOptions));
    } else {
      yield put(actions.getApplyIntakesFailure('No intakes found in university configuration'));
    }
  } catch (err: any) {
    yield put(actions.getApplyIntakesFailure(err.message));
  }
}

function* handleGetApplicantCourses(action: ReturnType<typeof actions.getApplicantCourses>): any {
  try {
    const context = yield getContext('sagaContext');
    const authState = yield select(getAuth);
    const { role } = authState;
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const res = yield call(
      api.getApplicantCourses,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.applicationId,
      action.payload.intakeId,
      action.payload.feeStatus,
      role,
    );
    if (res) {
      yield put(actions.getApplicantCoursesSuccess(res.courses));
    }
  } catch (err: any) {
    yield put(actions.getApplicantCoursesFailure(err.message));
  }
}
function* handleGetApplicantCourseChange(action: ReturnType<typeof actions.getApplicantCourseChange>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const res = yield call(
      api.getApplicantCourseChange,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.applicationId,
      action.payload.intakeId,
      action.payload.courseId,
    );
    if (res) {
      yield put(actions.getApplicantCourseChangeSuccess(res));
    }
  } catch (err: any) {
    yield put(actions.getApplicantCourseChangeFailure(err.message));
  }
}
function* handleUpdateApplicantRequirementStatus(
  action: ReturnType<typeof actions.updateApplicantRequirementStatus>,
): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const requirement = yield call(
      api.updateApplicantRequirementStatus,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
      action.payload.id,
      action.payload.status,
      action.payload.noteText,
      action.payload.conditionText,
    );
    yield put(actions.updateApplicantRequirementStatusSuccess(requirement));

    // A Provisional assessment may create a new FinalAcademicResults task on the backend.
    // Refetch the full offer so the new task appears in the UI without a manual page refresh.
    if (action.payload.status === ApplicantOfferRequirementTaskStatus.Provisional) {
      yield put(actions.getApplicantOffer(action.payload.applicantId, action.payload.uni, action.payload.offerId));
    }
  } catch (err: any) {
    yield put(actions.updatetApplicantRequirementStatusFailure(err.message));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* handleUpdateApplicantRequirementLevel(
  action: ReturnType<typeof actions.updateApplicantRequirementLevel>,
) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string | undefined = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleUpdateApplicantRequirementLevel');
    }
    const requirement = yield call(
      api.updateApplicantRequirementLevel,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
      action.payload.id,
      action.payload.level,
    );
    yield put(actions.updateApplicantRequirementLevelSuccess(requirement));
    if (requirement.type === ApplicantOfferRequirementTask.StaffOnly) {
      yield put(openToast({ title: 'Requirement level updated', status: 'success' }));
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'An unknown error occurred.';
    yield put(actions.updateApplicantRequirementLevelFailure(message));
    yield put(openToast({ title: message, status: 'error' }));
    // Resync the offer so stale UI state (e.g. a pending fulfilment reset that has since
    // been consumed by an applicant upload) is replaced with the current backend state.
    yield put(actions.getApplicantOffer(action.payload.applicantId, action.payload.uni, action.payload.offerId));
  }
}

function* handleUpdateApplicantOfferStatus(action: ReturnType<typeof actions.updateApplicantOfferStatus>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const offer = yield call(api.updateApplicantOfferStatus, idToken, {
      ...action.payload,
    });
    yield put(actions.updateApplicantOfferStatusSuccess(offer));
    yield put(actions.getApplicantOffer(action.payload.applicantId, action.payload.uni, action.payload.offerId));
    yield put(actions.getApplicantRecord(action.payload.applicantId, action.payload.applicationId, action.payload.uni));
    yield put(
      actions.getOfferTimeline(
        action.payload.uni,
        action.payload.applicantId,
        action.payload.applicationId,
        action.payload.offerId,
      ),
    );
  } catch (err: any) {
    yield put(actions.updateApplicantOfferStatusFailure(err.message));
  }
}

function* handleReinstateApplicantOffer(action: ReturnType<typeof actions.reinstateApplicantOffer>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    yield call(
      api.reinstateApplicantOffer,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
    );
    yield put(actions.reinstateApplicantOfferSuccess());
    yield put(actions.getApplicantOffer(action.payload.applicantId, action.payload.uni, action.payload.offerId));
    yield put(actions.getApplicantRecord(action.payload.applicantId, action.payload.applicationId, action.payload.uni));
  } catch (err: any) {
    yield put(actions.reinstateApplicantOfferFailure(err.message));
  }
}

function* handleUpdateApplicantOfferPaymentDepositStatus(
  action: ReturnType<typeof actions.updateApplicantOfferPaymentDepositStatus>,
): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const payment = yield call(
      api.updateApplicantOfferPaymentDepositStatus,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
      action.payload.status,
      action.payload.statusInternalNote,
    );
    yield put(actions.updateApplicantOfferPaymentDepositStatusSuccess(payment));
  } catch (err: any) {
    yield put(actions.updateApplicantOfferPaymentDepositStatusFailure(err.message));
  }
}

function* handleUpdateApplicantOfferPaymentProofStatus(
  action: ReturnType<typeof actions.updateApplicantOfferPaymentDepositStatus>,
): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const payment = yield call(
      api.updateApplicantOfferPaymentProofStatus,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
      action.payload.status,
      action.payload.statusInternalNote,
    );
    yield put(actions.updateApplicantOfferPaymentProofStatusSuccess(payment));
  } catch (err: any) {
    yield put(actions.updateApplicantOfferPaymentProofStatusFailure(err.message));
  }
}

function* handleDeleteApplicantOfferProofOfPaymentFile(
  action: ReturnType<typeof actions.deleteApplicantOfferProofOfPaymentFile>,
): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleDeleteApplicantOfferProofOfPaymentFile');
    }

    const { uni, applicantId, offerId, s3Key } = action.payload;

    // Get current offer data to find the current payment information
    const offerResponse = yield call(api.getApplicantOfferById, idToken, applicantId, uni, offerId);

    if ('error' in offerResponse || !offerResponse.offer?.payment?.proofOfPayment) {
      yield put(actions.deleteApplicantOfferProofOfPaymentFileFailure('Could not retrieve current payment data'));
      return;
    }

    const currentProofOfPayment = offerResponse.offer.payment.proofOfPayment;
    const FILE_NOT_FOUND_INDEX = -1;

    // Find the index of the file to remove
    const fileIndex = currentProofOfPayment.s3Keys?.findIndex((key: string) => key === s3Key);
    if (fileIndex === FILE_NOT_FOUND_INDEX) {
      yield put(actions.deleteApplicantOfferProofOfPaymentFileFailure('File not found'));
      return;
    }

    // Create updated arrays without the deleted file
    const updatedS3Keys = currentProofOfPayment.s3Keys?.filter((key: string) => key !== s3Key) || [];
    const updatedFileNames = currentProofOfPayment.fileName
      ? currentProofOfPayment.fileName.split(',').filter((_: string, index: number) => index !== fileIndex)
      : [];

    // Prepare the updated proof of payment data
    const updatedProofOfPayment = {
      fileName: updatedFileNames.length > 0 ? updatedFileNames.join(',') : null,
      s3Keys: updatedS3Keys.length > 0 ? updatedS3Keys : null,
      sKey: updatedS3Keys.length > 0 ? currentProofOfPayment.sKey : null,
    };

    // Call the API to update proof of payment
    const response = yield call(
      api.updateOfferProofOfPayment,
      idToken,
      uni,
      applicantId,
      offerId,
      updatedProofOfPayment,
    );

    if ('error' in response) {
      yield put(
        actions.deleteApplicantOfferProofOfPaymentFileFailure(`Failed to update proof of payment: ${response.error}`),
      );
      return;
    }

    // Create updated payment object with new proof of payment data
    const updatedPayment = {
      ...offerResponse.offer.payment,
      proofOfPayment: {
        ...currentProofOfPayment,
        fileName: updatedProofOfPayment.fileName,
        s3Keys: updatedProofOfPayment.s3Keys,
        sKey: updatedProofOfPayment.sKey,
        // Update URLs to remove the deleted file URL
        urls: currentProofOfPayment.urls?.filter((_: string, index: number) => index !== fileIndex) || [],
      },
    };

    // Dispatch success actions to update Redux state
    yield put(actions.deleteApplicantOfferProofOfPaymentFileSuccess(updatedPayment));
    // Also dispatch the document deletion success to trigger UI updates
    yield put({ type: '@@student/documents/DELETE_DOCUMENT_SUCCESS', payload: { s3key: s3Key } });
  } catch (err: any) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to delete proof of payment file';
    yield put(actions.deleteApplicantOfferProofOfPaymentFileFailure(`File deletion failed: ${errorMessage}`));
  }
}

function* handleUpdateApplicantOfferLetterText(action: ReturnType<typeof actions.updateApplicantOfferLetterText>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const offer = yield call(
      api.updateApplicantOfferLetterText,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
      action.payload.startText,
      action.payload.endText,
    );
    yield put(actions.updateApplicantOfferLetterTextSuccess(offer));
  } catch (err: any) {
    yield put(actions.updateApplicantOfferLetterTextFailure(err.message));
  }
}
function* handleUpdateApplicantProgramme(action: ReturnType<typeof actions.updateApplicantProgramme>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const offer = yield call(
      api.updateApplicantProgramme as any,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
      action.payload.programmeDetails,
    );
    yield put(actions.updateApplicantProgrammeSuccess(offer));
    yield put(actions.getApplicantOffer(action.payload.applicantId, action.payload.uni, action.payload.offerId));
    yield put(
      actions.getOfferTimeline(
        action.payload.uni,
        action.payload.applicantId,
        offer.applicationId,
        action.payload.offerId,
      ),
    );
    yield put(openToast({ title: 'The programme has been updated', status: 'success' }));
  } catch (err: any) {
    yield put(openToast({ title: 'The programme failed to update', status: 'error' }));
    console.log('error updating programme', JSON.stringify(err));
    yield put(actions.updateApplicantProgrammeFailure(err.message));
  }
}
function* handleCreateApplicantOffer(action: ReturnType<typeof actions.createApplicantOffer>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const res = yield call(
      api.createApplicantOffer,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.applicationId,
      action.payload.offer,
    );
    yield put(actions.createApplicantOfferSuccess(res));
    yield put(actions.getApplicantOffer(action.payload.applicantId, action.payload.uni, res.id));
    yield put(openToast({ title: 'The offer has been created', status: 'success' }));
  } catch (err: any) {
    console.log('offer creation failed', JSON.stringify(err), err.message);
    yield put(openToast({ title: 'The offer failed to create', status: 'error' }));
    yield put(actions.createApplicantOfferFailure(err.message));
  }
}

function* handleUnlinkApplicantEmail(action: ReturnType<typeof actions.unlinkApplicantEmail>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const { payload } = action;
    yield call(api.unlinkApplicantEmail, idToken, payload.uni, payload.applicantId);
    yield put(actions.unlinkApplicantEmailSuccess());
  } catch (err: any) {
    yield put(actions.unlinkApplicantEmailFailure(err.message));
  }
}

export function* handleCreateAdditionalRequirements(
  action: ReturnType<typeof actions.createAdditionalRequirements>,
): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleCreateAdditionalRequirements');
    }
    const requirements = yield call(
      api.saveAdditionalRequirements as any,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
      action.payload.requirements,
    );
    yield put(actions.createAdditionalRequirementsSuccess(requirements));
    // GET_APPLICANT_OFFER is the only place additionalRequirements is populated; refetch so the add dropdown omits the new task.
    yield put(actions.getApplicantOffer(action.payload.applicantId, action.payload.uni, action.payload.offerId));
  } catch (err: any) {
    yield put(actions.createAdditionalRequirementsFailure(err.message));
  }
}

function* handleStudentPinTag(action: ReturnType<typeof actions.studentsPinTag>): any {
  try {
    const uni = yield select(getUniId);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleStudentPinTag');
    }
    const { offerId, applicantId, tag } = action.payload;

    const tags = yield select(getTags);
    const targetTag = tags.find(({ id }) => id === tag.id);

    yield call(api.applyStudentsPinTag, idToken, uni, offerId, applicantId, targetTag);

    yield put(actions.studentsPinTagSuccess({ ...action.payload, tag: targetTag }));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.studentsPinTagFailure(err.message));
    } else {
      yield put(actions.studentsPinTagFailure('An unknown error occured.'));
    }
  }
}

function* handleStudentUnpinTag(action: ReturnType<typeof actions.studentsUnpinTag>): any {
  try {
    const uni = yield select(getUniId);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleStudentUnpinTag');
    }
    const { payload } = action;

    yield call(api.applyStudentsUnpinTag, idToken, uni, payload.offerId, payload.applicantId, payload.tag);

    yield put(actions.studentsUnpinTagSuccess({ ...payload }));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.studentsUnpinTagFailure(err.message));
    } else {
      yield put(actions.studentsUnpinTagFailure('An unknown error occured.'));
    }
  }
}

export function* handleUpdateApplicantDetails(action: ReturnType<typeof actions.updateApplicantDetails>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const response = yield call(
      api.updateApplicantDetails,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.applicationId,
      action.payload.data,
    );

    // If email was changed we have a new user in cognito so new applicantId
    const applicantId =
      response?.newApplicantId && response?.newApplicantId !== action.payload.applicantId
        ? response.newApplicantId
        : action.payload.applicantId;

    yield put(actions.updateApplicantDetailsSuccess({ applicantId, data: action.payload.data }));
  } catch (err: any) {
    yield put(actions.updateApplicantDetailsFailure(err.message));
    yield put(openToast({ title: err.message, status: 'error' }));
  }
}

function* dispatchLegacyActions(docTypes: string[], state: any): any {
  const { allDocuments = {} } = state;

  if (docTypes.includes('passport') || docTypes.includes('photoId')) {
    const identityDocs: { name: string; urls: string[] }[] = [];

    if (allDocuments.passport) {
      identityDocs.push(...allDocuments.passport);
    }
    if (allDocuments.photoId) {
      identityDocs.push(...allDocuments.photoId);
    }

    if (identityDocs.length > 0) {
      yield put(actions.getApplicantDocumentsSuccess(identityDocs));
    }
  }
}

function* handleGetAllDocumentsWithCounts(action: ReturnType<typeof actions.getAllDocumentsWithCounts>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetAllDocumentsWithCounts');
    }

    const { uni, applicantId, docTypes } = action.payload;

    const documentsWithCounts: any[] = [];

    const docTypePromises = docTypes.map(docType =>
      call(api.getApplicantDocuments, idToken, uni, applicantId, docType),
    );
    const docResponses = yield all(docTypePromises);

    const state = yield select((state: any) => state.apply);
    const { application } = state;

    for (let i = 0; i < docTypes.length; i++) {
      const docType = docTypes[i];
      const response = docResponses[i];
      const docs: Record<string, { signedUrls?: string[]; s3Keys?: string[]; fileNames?: string[] }> =
        response?.docs ?? {};

      const count = Object.values(docs).reduce(
        (acc, d) => acc + (d.signedUrls?.length ?? d.s3Keys?.length ?? d.fileNames?.length ?? 0),
        0,
      );

      const documents: { name: string; urls: string[] }[] = [];
      const urls = flattenUrls(docs);

      if (urls.length > 0) {
        let name = '';

        switch (docType) {
          case 'passport': {
            name = `${application?.nationality || ''} Passport`.trim();
            break;
          }
          case 'photoId': {
            name = `${application?.nationality || ''} Photo ID`.trim();
            break;
          }
          case 'englishTest': {
            const { englishTest } = application;
            const testType = englishTest?.type === 'Other' ? englishTest?.typeOther : englishTest?.type;
            name = `${testType || 'English Test'} Certificate`;
            break;
          }
          case 'cv': {
            const { givenName, familyName } = application;
            const applicantName = `${givenName || ''} ${familyName || ''}`.trim() || 'Applicant';
            name = `${applicantName} CV`;
            break;
          }
          case 'academicTranscript': {
            name = 'Academic Transcript';
            break;
          }
          case 'reference': {
            name = 'Reference Letter';
            break;
          }
          default: {
            name = docType.charAt(0).toUpperCase() + docType.slice(1);
          }
        }

        documents.push({ name, urls });
      }

      documentsWithCounts.push({
        docType,
        documents,
        count,
      });
    }

    yield put(actions.getAllDocumentsWithCountsSuccess(documentsWithCounts));

    const updatedState = yield select((state: any) => state.apply);
    yield call(dispatchLegacyActions, docTypes, updatedState);
  } catch (err: any) {
    yield put(actions.getAllDocumentsWithCountsFailure(err.message));
  }
}

function* handleGetApplyUniList(_action: ReturnType<typeof actions.getApplyUniList>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetApplyUniList');
    }
    const res = yield call(api.getUniList, idToken);
    if (res) {
      yield put(actions.getApplyUniListSuccess(res.universityIds));
    }
  } catch (err: any) {
    yield put(actions.getApplyUniListFailure(err));
  }
}

function* watchCreateAdditionalRequirements() {
  yield takeEvery(ApplyActionTypes.CREATE_ADDITIONAL_REQUIREMENTS, handleCreateAdditionalRequirements);
}

function* handleSelectStudiesToAssess(action: ReturnType<typeof actions.selectStudiesToAssess>): SagaIterator {
  try {
    const context = yield getContext('sagaContext');
    const idToken = (yield call(context.getIdToken)) as string | null;
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleSelectStudiesToAssess');
    }
    const res: ErrorResponseBody | unknown = yield call(
      api.selectStudiesToAssess,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
      action.payload.qualificationEntryId,
      action.payload.studyIds,
    );
    if (res && typeof res === 'object' && 'error' in res) {
      const body = (res as ErrorResponseBody).error;
      throw new Error(typeof body === 'string' ? body : 'Failed to update studies to assess');
    }
    yield put(actions.selectStudiesToAssessSuccess());
    yield put(actions.getApplicantOffer(action.payload.applicantId, action.payload.uni, action.payload.offerId));
    yield put(openToast({ title: 'Studies to assess updated', status: 'success' }));
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to update studies to assess';
    yield put(actions.selectStudiesToAssessFailure(message));
    yield put(openToast({ title: 'Failed to update studies to assess', status: 'error' }));
  }
}

function* watchSelectStudiesToAssess(): Generator<unknown, void, unknown> {
  yield takeEvery(ApplyActionTypes.SELECT_STUDIES_TO_ASSESS, handleSelectStudiesToAssess);
}

function* watchGetApplicantOffer() {
  yield takeLatest(ApplyActionTypes.GET_APPLICANT_OFFER, handleGetApplicantOffer);
}

function* watchGetApplicantIntakes() {
  yield takeEvery(ApplyActionTypes.GET_APPLICANT_INTAKES, handleGetApplicantIntakes);
}

function* watchGetApplyIntakes() {
  yield takeEvery(ApplyActionTypes.GET_APPLY_INTAKES, handleGetApplyIntakes);
}

function* watchGetApplicantCourses() {
  yield takeEvery(ApplyActionTypes.GET_APPLICANT_COURSES, handleGetApplicantCourses);
}

function* watchGetApplyUniList() {
  yield takeEvery(ApplyActionTypes.GET_APPLY_UNI_LIST, handleGetApplyUniList);
}
function* watchGetApplicantCourseChange() {
  yield takeEvery(ApplyActionTypes.GET_APPLICANT_COURSE_CHANGE, handleGetApplicantCourseChange);
}

function* watchUpdateApplicantRequirementStatus() {
  yield takeEvery(ApplyActionTypes.UPDATE_APPLICANT_REQUIREMENT_STATUS, handleUpdateApplicantRequirementStatus);
}

function* watchUpdateApplicantRequirementLevel(): Generator<unknown, void, unknown> {
  yield takeEvery(ApplyActionTypes.UPDATE_APPLICANT_REQUIREMENT_LEVEL, handleUpdateApplicantRequirementLevel);
}

function* handleExemptApplicantRequirement(
  action: ReturnType<typeof actions.exemptApplicantRequirement>,
): SagaIterator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string | undefined = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleExemptApplicantRequirement');
    }
    const response: IApplicantOffer | ErrorResponseBody = yield call(
      api.exemptApplicantRequirement,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
      action.payload.requirementId,
      action.payload.noteText,
    );
    if ('error' in response) throw new Error(typeof response.error === 'string' ? response.error : response.error.name);
    yield put(actions.exemptApplicantRequirementSuccess(response));
    yield put(openToast({ title: 'Requirement has been exempted', status: 'success' }));
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to exempt requirement';
    yield put(actions.exemptApplicantRequirementFailure(errorMessage));
    yield put(openToast({ title: 'Failed to exempt requirement', status: 'error' }));
  }
}

function* watchExemptApplicantRequirement(): SagaIterator {
  yield takeEvery(ApplyActionTypes.EXEMPT_APPLICANT_REQUIREMENT, handleExemptApplicantRequirement);
}

// Each call is committed independently — track the last successful offer so we can
// sync Redux state even if a later call fails (partial failure is better than stale state).
function* bulkExemptRequirements(
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
  requirementIds: string[],
  noteText?: string,
): SagaIterator<{ offer: IApplicantOffer | undefined; failed: boolean }> {
  let offer: IApplicantOffer | undefined;
  let failed = false;
  for (const requirementId of requirementIds) {
    try {
      const response: IApplicantOffer | ErrorResponseBody = yield call(
        api.exemptApplicantRequirement,
        idToken,
        applicantId,
        uni,
        offerId,
        requirementId,
        noteText,
      );
      if ('error' in response)
        throw new Error(typeof response.error === 'string' ? response.error : response.error.name);
      offer = response;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to exempt requirement';
      yield put(actions.exemptApplicantRequirementFailure(errorMessage));
      yield put(openToast({ title: 'Failed to exempt requirement', status: 'error' }));
      failed = true;
      break;
    }
  }
  return { offer, failed };
}

function* handleExemptApplicantInterviewRequirements(
  action: ReturnType<typeof actions.exemptApplicantInterviewRequirements>,
): SagaIterator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string | undefined = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleExemptApplicantInterviewRequirements');
    }
    const { applicantId, uni, offerId, requirementIds, noteText } = action.payload;
    if (requirementIds.length === 0) {
      yield put(openToast({ title: 'No tasks to exempt for this requirement', status: 'warning' }));
      return;
    }
    const { offer, failed }: { offer: IApplicantOffer | undefined; failed: boolean } = yield call(
      bulkExemptRequirements,
      idToken,
      applicantId,
      uni,
      offerId,
      requirementIds,
      noteText,
    );
    if (offer) yield put(actions.exemptApplicantRequirementSuccess(offer));
    if (!failed) yield put(openToast({ title: 'Requirement has been exempted', status: 'success' }));
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to exempt requirements';
    yield put(actions.exemptApplicantRequirementFailure(errorMessage));
    yield put(openToast({ title: 'Failed to exempt requirements', status: 'error' }));
  }
}

function* watchExemptApplicantInterviewRequirements(): SagaIterator {
  yield takeEvery(ApplyActionTypes.EXEMPT_APPLICANT_INTERVIEW_REQUIREMENTS, handleExemptApplicantInterviewRequirements);
}

function* handleExemptApplicantStaffRequirements(
  action: ReturnType<typeof actions.exemptApplicantStaffRequirements>,
): SagaIterator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string | undefined = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleExemptApplicantStaffRequirements');
    }
    const { applicantId, uni, offerId, requirementIds, noteText } = action.payload;
    if (requirementIds.length === 0) {
      yield put(openToast({ title: 'No tasks to exempt for this requirement', status: 'warning' }));
      return;
    }
    const { offer, failed }: { offer: IApplicantOffer | undefined; failed: boolean } = yield call(
      bulkExemptRequirements,
      idToken,
      applicantId,
      uni,
      offerId,
      requirementIds,
      noteText,
    );
    if (offer) yield put(actions.exemptApplicantRequirementSuccess(offer));
    if (!failed) yield put(openToast({ title: 'Requirement has been exempted', status: 'success' }));
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to exempt requirements';
    yield put(actions.exemptApplicantRequirementFailure(errorMessage));
    yield put(openToast({ title: 'Failed to exempt requirements', status: 'error' }));
  }
}

function* watchExemptApplicantStaffRequirements(): SagaIterator {
  yield takeEvery(ApplyActionTypes.EXEMPT_APPLICANT_STAFF_REQUIREMENTS, handleExemptApplicantStaffRequirements);
}

function* handleExemptApplicantOfferPayment(
  action: ReturnType<typeof actions.exemptApplicantOfferPayment>,
): SagaIterator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string | undefined = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleExemptApplicantOfferPayment');
    }
    const response: IApplicantOfferPayment | ErrorResponseBody = yield call(
      api.exemptApplicantOfferPayment,
      idToken,
      action.payload.applicantId,
      action.payload.uni,
      action.payload.offerId,
      action.payload.noteText,
    );
    if ('error' in response) throw new Error(typeof response.error === 'string' ? response.error : response.error.name);
    yield put(actions.exemptApplicantOfferPaymentSuccess(response));
    yield put(openToast({ title: 'Finance requirement has been exempted', status: 'success' }));
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to exempt requirement';
    yield put(actions.exemptApplicantOfferPaymentFailure(errorMessage));
    yield put(openToast({ title: 'Failed to exempt finance requirement', status: 'error' }));
  }
}

function* watchExemptApplicantOfferPayment(): SagaIterator {
  yield takeEvery(ApplyActionTypes.EXEMPT_APPLICANT_OFFER_PAYMENT, handleExemptApplicantOfferPayment);
}

function* watchUpdateApplicantOfferStatus() {
  yield takeEvery(ApplyActionTypes.UPDATE_APPLICANT_OFFER_STATUS, handleUpdateApplicantOfferStatus);
}

function* watchUpdateApplicantOfferPaymentDepositStatus() {
  yield takeEvery(
    ApplyActionTypes.UPDATE_APPLICANT_OFFER_PAYMENT_DEPOSIT_STATUS,
    handleUpdateApplicantOfferPaymentDepositStatus,
  );
}

function* watchUpdateApplicantOfferPaymentProofStatus() {
  yield takeEvery(
    ApplyActionTypes.UPDATE_APPLICANT_OFFER_PAYMENT_PROOF_STATUS,
    handleUpdateApplicantOfferPaymentProofStatus,
  );
}

function* watchDeleteApplicantOfferProofOfPaymentFile() {
  yield takeEvery(
    ApplyActionTypes.DELETE_APPLICANT_OFFER_PROOF_OF_PAYMENT_FILE,
    handleDeleteApplicantOfferProofOfPaymentFile,
  );
}

function* watchReinstateOfferStatus() {
  yield takeEvery(ApplyActionTypes.REINSTATE_APPLICANT_OFFER, handleReinstateApplicantOffer);
}

function* watchUpdateApplicantOfferLetterText() {
  yield takeEvery(ApplyActionTypes.UPDATE_APPLICANT_OFFER_LETTER_TEXT, handleUpdateApplicantOfferLetterText);
}

function* watchGetApplicantRecord() {
  yield takeEvery(ApplyActionTypes.GET_APPLICATION_RECORD, handleGetApplicationRecord);
}

function* watchGetApplyAuditLogRecord() {
  yield takeEvery(ApplyActionTypes.GET_APPLY_AUDIT_LOG, handleGetAuditLog);
}

function* watchGetOfferTimelineRecord() {
  yield takeEvery(ApplyActionTypes.GET_OFFER_TIMELINE, handleGetOfferTimeline);
}

function* watchGetApplyEmailRecord() {
  yield takeEvery(ApplyActionTypes.GET_APPLY_EMAILS, handleGetApplyEmails);
}
function* watchUpdateApplicantProgramme() {
  yield takeEvery(ApplyActionTypes.UPDATE_APPLICANT_PROGRAMME, handleUpdateApplicantProgramme);
}
function* watchCreateApplicantOffer() {
  yield takeEvery(ApplyActionTypes.CREATE_APPLICANT_OFFER, handleCreateApplicantOffer);
}

function* handleUniRegisterApplicant(action: ReturnType<typeof actions.uniStaffRegisterApplicant>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleAgentRegisterApplicant');
    }

    const {
      email,
      preferredName,
      universityId,
      nationality,
      dob,
      phoneNumber,
      feeStatus,
      disableEmailNotifications,
      programmeChoices,
    } = action.payload;

    const response = yield call(
      api.uniStaffRegisterApplicant,
      idToken,
      universityId,
      email.toLowerCase().trim(),
      preferredName,
      nationality,
      dob,
      phoneNumber,
      feeStatus,
      disableEmailNotifications,
      programmeChoices,
    );

    // console.log('API Registration response:', response);

    // Make sure the response has the correct format
    if (!response.data || !response.data.userId) {
      yield put(actions.uniStaffRegisterApplicantFailure(response));
      yield put(
        openToast({
          title: 'Registration failed',
          description: response.message,
          status: 'error',
        }),
      );
      return;
    }

    // Ensure we have a uni property in the data for consistent behavior
    if (!response.data.uni && response.data.universityId) {
      response.data.uni = response.data.universityId;
    } else if (!response.data.uni && !response.data.universityId) {
      // If neither exists, use the university ID from the action payload
      response.data.uni = universityId;
    }

    yield put(actions.uniStaffRegisterApplicantSuccess(response));
  } catch (err: any) {
    console.error('Registration error:', err);
    yield put(actions.uniStaffRegisterApplicantFailure(err.message));
    yield put(
      openToast({
        title: 'Registration failed',
        description: err.message || 'An error occurred during registration',
        status: 'error',
      }),
    );
  }
}

function* handleAgentRegisterApplicant(action: ReturnType<typeof actions.agentRegisterApplicant>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleAgentRegisterApplicant');
    }

    const {
      email,
      preferredName,
      universityId,
      nationality,
      dob,
      phoneNumber,
      feeStatus,
      programmeChoices,
      agentConsent,
    } = action.payload;

    const response = yield call(
      api.agentRegisterApplicant,
      idToken,
      universityId,
      email.toLowerCase().trim(),
      preferredName,
      nationality,
      dob,
      phoneNumber,
      feeStatus,
      programmeChoices,
      agentConsent,
    );

    // console.log('API Registration response:', response);

    // Make sure the response has the correct format
    if (!response.data || !response.data.userId) {
      yield put(actions.agentRegisterApplicantFailure(response));
      yield put(
        openToast({
          title: 'Registration failed',
          description: response.message,
          status: 'error',
        }),
      );
      return;
    }

    // Ensure we have a uni property in the data for consistent behavior
    if (!response.data.uni && response.data.universityId) {
      response.data.uni = response.data.universityId;
    } else if (!response.data.uni && !response.data.universityId) {
      // If neither exists, use the university ID from the action payload
      response.data.uni = universityId;
    }

    yield put(actions.agentRegisterApplicantSuccess(response));
  } catch (err: any) {
    console.error('Registration error:', err);
    yield put(actions.agentRegisterApplicantFailure(err.message));
    yield put(
      openToast({
        title: 'Registration failed',
        description: err.message || 'An error occurred during registration',
        status: 'error',
      }),
    );
  }
}

function* handlePartnerRegisterApplicant(action: ReturnType<typeof actions.partnerRegisterApplicant>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handlePartnerRegisterApplicant');
    }

    const { email, preferredName, universityId, nationality, dob, phoneNumber, feeStatus, programmeChoices } =
      action.payload;

    const response = yield call(
      api.partnerRegisterApplicant,
      idToken,
      universityId,
      email.toLowerCase().trim(),
      preferredName,
      nationality,
      dob,
      phoneNumber,
      feeStatus,
      programmeChoices,
    );

    // Make sure the response has the correct format
    if (!response.data || !response.data.userId) {
      yield put(actions.partnerRegisterApplicantFailure(response));
      yield put(
        openToast({
          title: 'Registration failed',
          description: response.message,
          status: 'error',
        }),
      );
      return;
    }

    // Ensure we have a uni property in the data for consistent behavior
    if (!response.data.uni && response.data.universityId) {
      response.data.uni = response.data.universityId;
    } else if (!response.data.uni && !response.data.universityId) {
      // If neither exists, use the university ID from the action payload
      response.data.uni = universityId;
    }

    yield put(actions.partnerRegisterApplicantSuccess(response));
  } catch (err: any) {
    console.error('Registration error:', err);
    yield put(actions.partnerRegisterApplicantSuccess(err.message));
    yield put(
      openToast({
        title: 'Registration failed',
        description: err.message || 'An error occurred during registration',
        status: 'error',
      }),
    );
  }
}

function* watchUniRegisterApplicant() {
  yield takeEvery(ApplyActionTypes.UNI_REGISTER_APPLICANT, handleUniRegisterApplicant);
}

function* watchAgentRegisterApplicant() {
  yield takeEvery(ApplyActionTypes.AGENT_REGISTER_APPLICANT, handleAgentRegisterApplicant);
}

function* watchPartnerRegisterApplicant() {
  yield takeEvery(ApplyActionTypes.PARTNER_REGISTER_APPLICANT, handlePartnerRegisterApplicant);
}

function* watchPinTag() {
  yield takeEvery(ApplyActionTypes.STUDENTS_TAGS_PIN, handleStudentPinTag);
}

function* watchUnpinTag() {
  yield takeEvery(ApplyActionTypes.STUDENTS_TAGS_UNPIN, handleStudentUnpinTag);
}

function* watchUpdateApplicantDetails() {
  yield takeLatest(ApplyActionTypes.UPDATE_APPLICANT_DETAILS, handleUpdateApplicantDetails);
}

function* watchGetAllDocumentsWithCounts() {
  yield takeEvery(ApplyActionTypes.GET_ALL_DOCUMENTS_WITH_COUNTS, handleGetAllDocumentsWithCounts);
}

function* watchUnlinkApplicantEmail() {
  yield takeEvery(ApplyActionTypes.UNLINK_APPLICANT_EMAIL, handleUnlinkApplicantEmail);
}

function* handleGetRouteStatus(action: ReturnType<typeof actions.getRouteStatus>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetRouteStatus');
    }

    const { uni, applicantId, applicationId } = action.payload;
    const res: RouteStatusResponse | ErrorResponseBody = yield call(
      api.getApplicantRouteStatus,
      idToken,
      uni,
      applicantId,
      applicationId,
    );

    if (res && 'error' in res) {
      const errorMessage = typeof res.error === 'string' ? res.error : 'Failed to fetch route status';
      yield put(actions.getRouteStatusFailure(errorMessage));
    } else if (res && 'sections' in res) {
      yield put(
        actions.getRouteStatusSuccess({
          sections: res.sections,
          defaultTask: res.defaultTask,
          applicantId,
          applicationId,
        }),
      );
    }
  } catch (err: any) {
    yield put(actions.getRouteStatusFailure(err.message));
  }
}

function* watchGetRouteStatus() {
  yield takeEvery(ApplyActionTypes.GET_ROUTE_STATUS, handleGetRouteStatus);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* handleRequestTasks(action: ReturnType<typeof actions.requestTasks>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: string | undefined = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleRequestTasks');
    }
    const offer = yield call(
      api.requestTasks,
      idToken,
      action.payload.uni,
      action.payload.applicantId,
      action.payload.offerId,
      action.payload.level,
    );
    yield put(actions.requestTasksSuccess(offer));
    yield put(actions.getApplicantOffer(action.payload.applicantId, action.payload.uni, action.payload.offerId));
    yield put(actions.getApplicantRecord(action.payload.applicantId, action.payload.applicationId, action.payload.uni));
    yield put(
      actions.getOfferTimeline(
        action.payload.uni,
        action.payload.applicantId,
        action.payload.applicationId,
        action.payload.offerId,
      ),
    );
    const isPreOffer = action.payload.level === 'PreOffer';
    const toastTitle = isPreOffer ? 'Pre-offer tasks requested' : 'Enrolment tasks requested';
    yield put(openToast({ title: toastTitle, status: 'success' }));
  } catch (err: unknown) {
    yield put(actions.requestTasksFailure(err as Error));
    const isPreOffer = action.payload.level === 'PreOffer';
    const errorTitle = isPreOffer ? 'Failed to request pre-offer tasks' : 'Failed to request enrolment tasks';
    yield put(openToast({ title: errorTitle, status: 'error' }));
  }
}

function* watchRequestTasks(): Generator<ForkEffect<never>, void, unknown> {
  yield takeEvery(ApplyActionTypes.REQUEST_TASKS, handleRequestTasks);
}

function* updateDuplicateHandler(action: ReturnType<typeof actions.updateDuplicate>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetRouteStatus');
    }

    const { uni, userId, applicationId, integration, isDuplicate, selectedStudentNo } = action.payload;

    const res: ResolveDuplicateHandlerResponse = yield call(api.updateDuplicate, {
      integration,
      idToken,
      uni,
      userId,
      applicationId,
      isDuplicate,
      studentNo: selectedStudentNo,
    });

    yield put(actions.updateDuplicateSuccess(res.resolved));

    yield put(actions.getApplicantRecord(userId, applicationId, uni));
  } catch (e) {
    yield put(actions.updateDuplicateFailure(e.message));
  }
}
function* watchUpdateDuplicate() {
  yield takeEvery(ApplyActionTypes.UPDATE_DUPLICATE, updateDuplicateHandler);
}

function* getCalendlySchedulingInfoHandler(action: {
  type: string;
  payload: { uni: string; eventType: string };
}): Generator {
  const { uni, eventType } = action.payload;
  try {
    const context = yield getContext('sagaContext');
    const idToken: string = yield call(context.getIdToken);

    if (!idToken) {
      yield put(logout());
      yield put(actions.getCalendlySchedulingInfoFailure({ eventType, error: new Error('No idToken') }));
      return;
    }
    const res: Awaited<ReturnType<typeof api.getCalendlySchedulingInfo>> = yield call(
      api.getCalendlySchedulingInfo,
      idToken,
      uni,
      eventType,
    );

    yield put(actions.getCalendlySchedulingInfoSuccess({ eventType, ...res }));
  } catch (e) {
    yield put(actions.getCalendlySchedulingInfoFailure({ eventType, error: e }));
  }
}

function* watchGetCalendlySchedulingInfo(): Generator {
  yield takeEvery(ApplyActionTypes.GET_CALENDLY_SCHEDULING_INFO, getCalendlySchedulingInfoHandler);
}

function* ApplicantSaga(): Generator {
  yield all([fork(watchGetApplicantRecord)]);
  yield all([fork(watchGetApplyAuditLogRecord)]);
  yield all([fork(watchGetOfferTimelineRecord)]);
  yield all([fork(watchGetApplyEmailRecord)]);
  yield all([fork(watchGetApplicantOffer)]);
  yield all([fork(watchUpdateApplicantRequirementStatus)]);
  yield all([fork(watchUpdateApplicantRequirementLevel)]);
  yield all([fork(watchExemptApplicantRequirement)]);
  yield all([fork(watchExemptApplicantInterviewRequirements)]);
  yield all([fork(watchExemptApplicantStaffRequirements)]);
  yield all([fork(watchExemptApplicantOfferPayment)]);
  yield all([fork(watchUpdateApplicantOfferStatus)]);
  yield all([fork(watchUpdateApplicantOfferPaymentDepositStatus)]);
  yield all([fork(watchUpdateApplicantOfferPaymentProofStatus)]);
  yield all([fork(watchDeleteApplicantOfferProofOfPaymentFile)]);
  yield all([fork(watchReinstateOfferStatus)]);
  yield all([fork(watchUpdateApplicantOfferLetterText)]);
  yield all([fork(watchGetApplicantIntakes)]);
  yield all([fork(watchGetApplyIntakes)]);
  yield all([fork(watchGetApplicantCourses)]);
  yield all([fork(watchGetApplicantCourseChange)]);
  yield all([fork(watchUpdateApplicantProgramme)]);
  yield all([fork(watchCreateApplicantOffer)]);
  yield all([fork(watchCreateAdditionalRequirements)]);
  yield all([fork(watchSelectStudiesToAssess)]);

  // registration sagas
  yield all([fork(watchAgentRegisterApplicant)]);
  yield all([fork(watchUniRegisterApplicant)]);
  yield all([fork(watchPartnerRegisterApplicant)]);
  // ---

  yield all([fork(watchPinTag)]);
  yield all([fork(watchUnpinTag)]);
  yield all([fork(watchUpdateApplicantDetails)]);
  yield all([fork(watchUnlinkApplicantEmail)]);
  yield all([fork(watchGetAllDocumentsWithCounts)]);
  yield all([fork(watchGetApplyUniList)]);
  yield all([fork(watchGetRouteStatus)]);

  yield fork(watchUpdateDuplicate);
  yield fork(watchRequestTasks);
  yield fork(watchGetCalendlySchedulingInfo);
}

export default ApplicantSaga;
